import { Link } from 'expo-router';
import React, { useCallback, useMemo } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';

import { IconInfoFill, Text, XStack, createMqStyles, tokens } from '@fhs/ui';
import { EventTypes, useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import { SimplyOfferEventNames } from '../../analytics';
import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';

interface Props {
  style?: StyleProp<ViewStyle>;
}

export const SwitchServiceModeInline = ({ style }: Props) => {
  const { logRBIEvent } = useCRMEventsContext();
  const mqStyles = useMqStyles();
  const { serviceMode, isAuthenticated } = useLoyaltyLegacyStates();
  const link = useMemo(() => {
    if (serviceMode === 'DELIVERY') {
      return '/store-locator/service-mode';
    }
    return '/store-locator/address';
  }, [serviceMode]);
  const text = useMemo(() => {
    if (serviceMode === 'DELIVERY') {
      return 'Switch to Pickup';
    }
    return 'Switch to Delivery';
  }, [serviceMode]);

  /** Full sentence on the link so SR doesn’t only read the underlined words. */
  const switchServiceLinkA11yLabel = useMemo(() => {
    if (!isAuthenticated) {
      return `Looking for more offers? ${text} or Sign In for additional options.`;
    }
    return `Looking for more offers? ${text} for additional options.`;
  }, [isAuthenticated, text]);

  const signInLinkA11yLabel =
    'Looking for more offers? Sign In for additional options, or change your service mode.';

  const handlePress = useCallback(() => {
    logRBIEvent({
      name: SimplyOfferEventNames.OFFER_SWITCH_SERVICE_MODE,
      type: EventTypes.Other,
      attributes: {
        currentServiceMode: serviceMode as String,
      },
    });
  }, [serviceMode, logRBIEvent]);

  return (
    <XStack style={[mqStyles.alert, style]}>
      <View
        accessible={false}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        <IconInfoFill color={tokens.colors.$houseRed} size={20} />
      </View>
      {/* accessible={false}: avoid SR reading static chunks separately; links carry full sentences. */}
      <Text.Ui accessible={false} style={[mqStyles.inlineText]} size="md">
        Looking for more offers?{' '}
        <Link
          accessibilityLabel={switchServiceLinkA11yLabel}
          accessibilityRole="link"
          href={link}
          onPress={handlePress}
        >
          <Text.Ui accessible={false} style={mqStyles.textHighlighted} size="md">
            {text}
          </Text.Ui>
        </Link>
        {!isAuthenticated && (
          <>
            {' or '}
            <Link
              accessibilityLabel={signInLinkA11yLabel}
              accessibilityRole="link"
              href={routes.signIn}
            >
              <Text.Ui accessible={false} style={mqStyles.textHighlighted} size="md">
                Sign In
              </Text.Ui>
            </Link>
          </>
        )}{' '}
        for additional options.
      </Text.Ui>
    </XStack>
  );
};

const useMqStyles = createMqStyles({
  alert: {
    $base: {
      flex: 1,
      gap: 8,
      alignItems: 'center',
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderRadius: 8,
      backgroundColor: tokens.colors.$houseLight,
    },
  },
  inlineText: {
    $base: {
      fontSize: 12,
      flex: 1,
    },
    $gteDesktop: {
      fontSize: 14,
    },
  },
  textHighlighted: {
    $base: {
      textDecorationLine: 'underline',
      fontSize: 12,
    },
    $gteDesktop: {
      fontSize: 14,
    },
  },
});
